import React, {useState, useCallback, useMemo} from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import {Form} from 'react-final-form';
import {FORM_ERROR} from 'final-form';
import {MenuItem, Paper} from '@material-ui/core';

import {
  FieldsContainer,
  FormTitle,
  FormSectionTitle,
  SubmitButton,
} from '../../utils/commonStyledComponents';
import StatusAlert from '../StatusAlert';
import ContactInfo from './ContactInfo';
import {FormError, TextField} from './';
import {mutators, participantInitialValues} from './utils';
import {validateUpdateParticipant} from '../Form/validation';
import {capitalize} from '../../utils/formatString';
import {updateMemberRelationships} from '../../utils/api';

import {
  RELATION_GENITORE,
  RELATION_FIGLIO,
  RELATION_COMPAGNO,
  RELATION_CONIUGE,
  RELATION_ALTRO,
} from '../../utils/constants';

const RELATIONSHIP_TYPES = [
  RELATION_GENITORE,
  RELATION_FIGLIO,
  RELATION_COMPAGNO,
  RELATION_CONIUGE,
  RELATION_ALTRO,
];

const UpdateParticipant = ({participant, type, id, cities}) => {
  const [showAlert, setShowAlert] = useState(false);

  const onSubmit = useCallback(
    async data => {
      try {
        await updateMemberRelationships(id, data);

        setShowAlert(true);
        setTimeout(() => {
          setShowAlert(false);
        }, 4000);
      } catch (e) {
        const {data} = e.response || {};

        if (data.errors) {
          const {type, member} = data.errors;
          return {...member, type};
        }

        return {
          [FORM_ERROR]:
            data.message ||
            'Si è verificato un errore nella comunicazione con il server',
        };
      }
    },
    [id, setShowAlert]
  );

  const initialValues = useMemo(
    () => participantInitialValues(participant, type),
    [participant, type]
  );

  return (
    <Form
      onSubmit={onSubmit}
      mutators={mutators}
      initialValues={initialValues}
      validate={validateUpdateParticipant}
      render={({
        values,
        form,
        handleSubmit,
        submitting,
        submitError,
        hasValidationErrors,
      }) => (
        <form onSubmit={handleSubmit} noValidate={true}>
          <div className="gc-card2" style={{marginBottom: 24}}>
            <div className="gc-card2__head gc-card2__head--diamond">
              <span className="gc-iconbadge gc-iconbadge--diamond">
                <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                </svg>
              </span>
              <div>
                <p className="gc-card2__eyebrow">Partecipante</p>
                <h3 className="gc-card2__title">
                  {participant && (participant.firstName || participant.lastName)
                    ? `${participant.firstName || ''} ${participant.lastName || ''}`.trim()
                    : 'Partecipante collegato'}
                </h3>
              </div>
            </div>
            <div className="gc-card2__body">
              <FieldsContainer>
                {!submitting && submitError && (
                  <FormError>{submitError}</FormError>
                )}

                {/* FormTitle legacy mantenuto come anchor (nascosto visivamente, l'head card lo sostituisce) */}
                <FormTitle style={{display: 'none'}}>Participante</FormTitle>

                <Select name="type" label="Relazione" required>
                  {RELATIONSHIP_TYPES.map((relation, index) => (
                    <MenuItem value={relation} key={index}>
                      {capitalize(relation)}
                    </MenuItem>
                  ))}
                </Select>

                <FormSectionTitle>Informazioni Partecipante</FormSectionTitle>

                <ContactInfo values={values} cities={cities} form={form} />
              </FieldsContainer>

              <SubmitButton
                text="Aggiorna"
                loading={submitting}
                disabled={submitting || hasValidationErrors}
              />

              <StatusAlert visible={showAlert}>
                Aggiornamento avvenuto con successo
              </StatusAlert>
            </div>
          </div>
        </form>
      )}
    />
  );
};

UpdateParticipant.propTypes = {
  cities: PropTypes.array,
  participant: PropTypes.object,
  type: PropTypes.string,
  id: PropTypes.number,
};

export default UpdateParticipant;

const Select = styled(TextField).attrs({select: true, disabled: true})`
  width: 200px;
  margin: 8px;
`;
